import { AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { coerceArray } from '@partners/utils';
import { IValidationHandler, IValidatorState } from '../model';

/**
 * @description
 * Rebuild an array of ValidatorFn to reflect each validator's disabled | active state.
 *
 * When a state indicates a validatorFn is disabled it is removed off the control's validators
 * array and vice versa. this is how a validator is toggled on|off. Each validation handler
 * contains a single or multiple type|s of the custom ValidatorFn
 *
 * For Examples:
 * 1. LengthRangeValidation handler contains both (a) minLength (b) maxLength types
 * 2. HebrewNameValidation handler contains both (a) pattern (b) minLength (c) maxLength types
 *
 * Each validator state relates to a specific custom validator (not to the wrapper class!), so -
 * in example 2 we can disable "minLength" validator solely by passing { type: "minLength", disabled: true }
 *
 * @param validationHandlers a wrapper class for single or multiple custom ValidatorFn|s
 * @param validationState an array of validators' state
 * @returns a new array of ValidatorFn
 */
export function composeValidators(
  validationHandlers: IValidationHandler[] = [],
  validationStates: IValidatorState[] = []
): ValidatorFn[] {
  let vfn = new Array<ValidatorFn>();

  /** iterate the validation handlers collection */
  validationHandlers.forEach((vldHandler: IValidationHandler) => {
    let { type, validators } = vldHandler;
    if (!validators?.length) {
      return;
    }

    type = coerceArray(type);

    type.forEach((validatorType) => {
      const modifiedValidator = validationStates.find(({ type }) => validatorType === type);

      if (modifiedValidator?.disabled) {
        return;
      }

      vfn = vfn.concat(vldHandler.validators as ValidatorFn[]);
    });
  });

  return vfn;
}

/**
 * @description
 * Same as "composeValidators() above, but works on the asyncValidatorFn" collection of
 * the form control
 */
export function composeAsyncValidators(
  validationHandlers: IValidationHandler[] = [],
  validationStates: IValidatorState[] = []
): AsyncValidatorFn[] {
  let avfn = new Array<AsyncValidatorFn>();

  /** iterate the validation handlers collection */
  validationHandlers.forEach((vldHandler: IValidationHandler) => {
    let { type, asyncValidators } = vldHandler;
    if (!asyncValidators?.length) {
      return;
    }

    type = coerceArray(type);

    type.forEach((validatorType) => {
      const modifiedValidator = validationStates.find(({ type }) => validatorType === type);

      if (modifiedValidator?.disabled) {
        return;
      }

      avfn = avfn.concat(vldHandler.asyncValidators as AsyncValidatorFn[]);
    });
  });

  return avfn;
}
